import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { userActions } from '../../actions/user.action';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { services } from '../../services';
import { UserFormData, UserRole } from '../../types/user';
import { Category } from '../../types/users/user.create';
import { Role } from '../../types/users/user.filters';

interface AddUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: (user: UserFormData) => void;
}

const roles: { value: UserRole; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'INVESTOR', label: 'Investor' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'ASSET_MANAGER', label: 'Asset Manager' },
  { value: 'COMPLIANCE_OFFICER', label: 'Compliance Officer' },
  { value: 'ADMIN', label: 'Admin' },
];

export function AddUserModal({ open, onOpenChange, onAdd }: AddUserModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    role: 'USER',
    walletAddress: '',
    username: '',
    bio: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<(Category & { _id?: string; name?: string })[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [rolesList, setRolesList] = useState<(Role & { _id?: string })[]>([]);

  // Fetch categories and roles when modal opens
  useEffect(() => {
    if (open) {
      // Fetch categories
      userActions
        .getCategoriesForAdmin()
        .then((response) => {
          if (response?.data) {
            setCategories(response.data);
          }
        })
        .catch(() => {
          // Error handled silently - using empty function to suppress ESLint
          void 0;
        });

      // Fetch roles
      userActions
        .getRoles()
        .then((response) => {
          if (Array.isArray(response) && response.length > 0) {
            setRolesList(response);
          }
        })
        .catch(() => {
          // Error handled silently - using empty function to suppress ESLint
          void 0;
        });
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast({ description: 'Please provide an email address', variant: 'destructive' });
      return;
    }

    if (!formData.walletAddress) {
      toast({ description: 'Please provide a wallet address', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Find the role ID from the role name
      const selectedRole = rolesList.find(
        (r) => r.roleName === formData.role || r.roleName === formData.role.toUpperCase()
      );

      if (!selectedRole) {
        toast({ description: 'Invalid role selected', variant: 'destructive' });
        setIsSubmitting(false);
        return;
      }

      const params = {
        name: formData.name || null,
        email: formData.email || null,
        bio: formData.bio || null,
        category: selectedCategories.length > 0 ? selectedCategories : null,
        walletAddress: formData.walletAddress,
        profile: formData.profile || null,
        username: formData.username || null,
        roleId: selectedRole.id || selectedRole._id,
        status: 'PENDING',
      };

      const response = await services.post('/user/addNewUserByAdmin', params);

      if (response?.status === 200 || response?.status === 201) {
        toast({
          description: `User ${formData.email} has been created successfully`,
          variant: 'default',
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          role: 'USER',
          walletAddress: '',
          username: '',
          bio: '',
        });
        setSelectedCategories([]);

        // Invalidate queries to refresh user list
        queryClient.invalidateQueries({ queryKey: ['users'] });

        onAdd?.(formData);
        onOpenChange(false);
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };

      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        toast({ description: 'Unauthorized Request', variant: 'destructive' });
        localStorage.removeItem('token');
      } else if (axiosError?.response?.status === 400) {
        toast({
          description: axiosError?.response?.data?.message || 'Invalid user data',
          variant: 'destructive',
        });
      } else {
        toast({
          description: axiosError?.response?.data?.message || 'Failed to create user',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Invite New User</DialogTitle>
          <DialogDescription>
            Create a new user account. The user will receive an invitation to join the platform.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='email'>
              Email Address <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='email'
              type='email'
              placeholder='user@example.com'
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='name'>Full Name (Optional)</Label>
            <Input
              id='name'
              placeholder='John Smith'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='username'>Username (Optional)</Label>
            <Input
              id='username'
              placeholder='johndoe'
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='role'>
              Role <span className='text-destructive'>*</span>
            </Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value as UserRole })}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select a role' />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='wallet'>
              Wallet Address <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='wallet'
              placeholder='0x...'
              value={formData.walletAddress}
              onChange={(e) => setFormData({ ...formData, walletAddress: e.target.value })}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='bio'>Bio (Optional)</Label>
            <Input
              id='bio'
              placeholder='User bio'
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            />
          </div>

          {categories.length > 0 && (
            <div className='space-y-2'>
              <Label>Categories (Optional)</Label>
              <div className='max-h-32 overflow-y-auto border rounded-md p-2 space-y-1'>
                {categories.map((category) => (
                  <label
                    key={category.id || category._id}
                    className='flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-1 rounded'
                  >
                    <input
                      type='checkbox'
                      checked={selectedCategories.includes(category.id || category._id)}
                      onChange={() => handleCategoryToggle(category.id || category._id)}
                      className='rounded'
                    />
                    <span className='text-sm'>
                      {typeof category.categoryName === 'object' && category.categoryName?.en
                        ? category.categoryName.en
                        : typeof category.categoryName === 'string'
                          ? category.categoryName
                          : category.name || 'Unnamed Category'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className='pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
